import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  useTheme,
  Tabs,
  Tab,
  CardContent,
  Stack,
  FormControlLabel,
  Switch as MuiSwitch
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from '@mui/material/styles';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import PageTitle from '@Components/PageTitle/PageTitle';
import * as DefaultRoutes from '@Routes/DefaultRoutes';
import { StyledCard } from '@Pages/RegulatoryMember/ExitRequestDetail/styles';
import { RequestDetailsTab } from '../ExitPlanForm/Tabs/RequestDetailsTab/RequestDetailsTab';
import { AffectedProductsTab } from '../ExitPlanForm/Tabs/AffectedProductsTab/AffectedProductsTab';
import { ExitPlanFormSection } from '../ExitPlanForm/ExitPlanFormSection/ExitPlanFormSection'; // <-- import for form section
import { PlanStatusTab } from '../ExitPlanForm/Tabs/PlanStatusTab/PlanStatusTab';
import { SupportDetailsTab } from '../ExitPlanForm/Tabs/Support/SupportDetailsTab/SupportDetailsTab';
import { SupportRequestTab } from '../ExitPlanForm/Tabs/Support/SupportRequestTab/SupportRequestTab'; // <-- import for support request tab
import SupportTab from '../ExitPlanForm/Tabs/Support/SupportTab'; // <-- import for new support requests tab
import {
  PageContainer,
  HeaderContainer,
  cardTransition,
  cardContentStyles,
} from '@Pages/RnDMember/ExitRequestResponseUnified/ExitRequestResponseStyles';
import {backButtonStyles, sectionTitleStyles} from "../ExitRequestResponseUnified/ExitRequestResponseStyles";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import {SectionTitle} from "../../RegulatoryMember/ExitRequestDetail/styles";
import { PlanViewerWithPopup } from '../../RegulatoryMember/ExitRequestDetail/PlanViewerWithPopup';
import themeCreator from '@Themes/themeCreator';
import { DeletePopup } from "@Components/Popup/DeletePopup";
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";

// Create a styled switch component for the mode toggle - smaller and more modern
const ModeSwitch = styled((props) => (
  <MuiSwitch 
    focusVisibleClassName=".Mui-focusVisible" 
    disableRipple 
    {...props} 
  />
))(({ theme }) => ({
  width: 34,
  height: 18,
  padding: 0,
  margin: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '250ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main,
        opacity: 0.85,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 250,
    }),
  },
}));

const ExitPlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentUserRole = useSelector((state) => state.user.role);

  // State for the plan details
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // For edit mode: track selected product ids (impacted only)
  const initialImpactedIds = planData?.request?.response?.products?.filter(p => p.isAffected === true).map(p => p.id) || [];
  const [editSelectedProductIds, setEditSelectedProductIds] = useState(initialImpactedIds);

  // For edit mode: track editable fields
  const [editExitDate, setEditExitDate] = useState(null);
  const [editReplacementPlan, setEditReplacementPlan] = useState('');
  const [editAdditionalComments, setEditAdditionalComments] = useState('');
  const [editEndOfProductionDate, setEditEndOfProductionDate] = useState(null);
  const [editEndOfImportationDate, setEditEndOfImportationDate] = useState(null);
  const [editLiquidationPlan, setEditLiquidationPlan] = useState('');
  const [editFormErrors, setEditFormErrors] = useState({});

  // Track initial values for dirty check
  const [initialPlanSnapshot, setInitialPlanSnapshot] = useState(null);

  // Choose scenario for dummy data
  const scenario = 1; // Change this number (1, 2, 3, ...) to preview different cases

  // Data loading considering location state
  useEffect(() => {
    // If we have data from navigation (from the form edit)
    if (location.state?.planData) {
      setPlanData(location.state.planData);
      setLoading(false);
      return; // Skip fetching
    }

    // Otherwise, fetch data
    setLoading(true);
    setTimeout(() => {
      // --- SCENARIO 1: Both support provided, both members, both commented and approved ---
      const scenario1 = {
        id: planId,
        request: {
          id: 1,
          ingredient: {
            id: 1,
            name: "Vanilla Extract",
            casNumber: "8024-06-4",
            description: "Natural flavoring derived from vanilla beans"
          },
          primaryDate: "2023-12-15",
          primaryDateType: "OFF_SHELF_BAN_DATE",
          createdAt: "2023-11-15",
          responseDate: "2023-11-20",
          user: {
            username: "John Doe",
            email: "john.doe@example.com",
            role: "Regulatory Officer"
          },
          restrictionType: "banning", // or "banning"
          restrictionDetails: "Ingredient will be completely banned due to regulatory changes.",
          regions: ["North America", "Europe"],
          rationale: "Recent studies have shown potential environmental impact when this ingredient is released during production.",
          response: {
            status: "incompliant", // or "compliant"
            feedback: "We have several products that rely heavily on this ingredient and need time to develop alternatives. We will need to create an exit plan for a phased approach to reformulation.",
            products: [
              { id: 1, identifier: 'PRD001', cucNumber: '12345', name: 'Shampoo X', isAffected: true, comment: "This is our flagship product and uses this ingredient as a key component." },
              { id: 3, identifier: 'PRD003', cucNumber: '12347', name: 'Lotion Z', isAffected: true, comment: "Recent formulation revision increased dependency on this ingredient." },
              { id: 5, identifier: 'PRD005', cucNumber: '12349', name: 'Face Cream B', isAffected: false, comment: "This product uses a synthetic alternative already." },
            ]
          }
        },
        exitPlanStatus: "ready",
        exitDate: "2023-12-10",
        replacementPlan: "Replace with synthetic vanilla alternative in all impacted products.",
        additionalComments: "We will need to coordinate with suppliers to ensure timely delivery of the replacement ingredient.",
        createdAt: "2023-11-25",
        updatedAt: "2023-11-28",
        needsSupport: true,
        marketingSupport: true,
        supplyChainSupport: true,
        marketingSupportProvided: true,
        supplyChainSupportProvided: true,
        selectedMarketingMember: {
          id: 1, name: "Sarah Johnson", email: "sarah.johnson@company.com", username: "sjohnson", role: "MARKETING"
        },
        selectedSupplyChainMember: {
          id: 2, name: "Ahmed Mostafa", email: "ahmed.mostafa@company.com", username: "amostafa", role: "SUPPLY CHAIN"
        },
        comments: [
          {
            id: 101,
            user: { id: 1, name: "Sarah Johnson", email: "sarah.johnson@company.com", username: "sjohnson", role: "MARKETING" },
            isApproved: false,
            comment: "Marketing approves the plan and will update materials. Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.",
            createdAt: "2023-12-01T09:15:00Z"
          },
          {
            id: 102,
            user: { id: 2, name: "Ahmed Mostafa", email: "ahmed.mostafa@company.com", username: "amostafa", role: "SUPPLY CHAIN" },
            isApproved: true,
            comment: "Marketing approves the plan and will update materials. Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.Marketing approves the plan and will update materials.",
            createdAt: "2023-12-01T10:00:00Z"
          }
        ],
        reviewStatus: "pending",
        reviewComments: "Both departments have approved the plan."
      };

      // --- SCENARIO 2: Only marketing support requested, not yet provided, no comment ---
      const scenario2 = {
        ...scenario1,
        supplyChainSupport: false,
        supplyChainSupportProvided: false,
        selectedSupplyChainMember: null,
        comments: [
          {
            id: 101,
            user: { id: 1, name: "Sarah Johnson", email: "sarah.johnson@company.com", username: "sjohnson", role: "MARKETING" },
            isApproved: false,
            comment: "",
            createdAt: null
          }
        ],
        marketingSupportProvided: false,
        reviewComments: ""
      };

      // --- SCENARIO 3: Both support requested, marketing provided and commented, supply chain not provided ---
      const scenario3 = {
        ...scenario1,
        marketingSupportProvided: true,
        supplyChainSupportProvided: false,
        comments: [
          {
            id: 101,
            user: { id: 1, name: "Sarah Johnson", email: "sarah.johnson@company.com", username: "sjohnson", role: "MARKETING" },
            isApproved: true,
            comment: "Marketing approves the plan.Marketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the planMarketing approves the plan",
            createdAt: "2023-12-01T09:15:00Z"
          }
        ],
        reviewComments: "Marketing approved, waiting for supply chain."
      };

      // --- SCENARIO 4: No support requested ---
      const scenario4 = {
        ...scenario1,
        needsSupport: false,
        marketingSupport: false,
        supplyChainSupport: false,
        marketingSupportProvided: false,
        supplyChainSupportProvided: false,
        selectedMarketingMember: null,
        selectedSupplyChainMember: null,
        comments: [],
        reviewComments: ""
      };

      // --- SCENARIO 5: Both support requested, no members selected ---
      const scenario5 = {
        ...scenario1,
        selectedMarketingMember: null,
        selectedSupplyChainMember: null,
        comments: [],
        marketingSupportProvided: false,
        supplyChainSupportProvided: false,
        reviewComments: ""
      };

      // Choose scenario
      let data;
      switch (scenario) {
        case 1: data = scenario1; break;
        case 2: data = scenario2; break;
        case 3: data = scenario3; break;
        case 4: data = scenario4; break;
        case 5: data = scenario5; break;
        default: data = scenario1;
      }

      setPlanData(data);
      setLoading(false);
    }, 1000);
  }, [planId, location.state]);

  const handleBackClick = () => navigate(
    currentUserRole === "RD_MANAGER" ? DefaultRoutes.rdManagerExitPlans : DefaultRoutes.rdExitPlans
  );
  
  const handleEditClick = () => {
    // Navigate to the edit mode passing the current data
    // navigate(DefaultRoutes.rdExitPlanEdit.replace(':planId', planId), {
    //   state: { planData, skipLoading: true }  // Add skipLoading flag
    // });
  };
  
  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const canEdit = () => {
    return true;
  };

  // Helper: parse date string to Date object or null
  const parseDate = (date) => {
    if (!date) return null;
    if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : d;
  };

  // Sync edit fields with planData when entering edit mode or planData changes
  useEffect(() => {
    if (!planData) return;
    // Use productsInThisPlan if available, otherwise fallback to impacted products
    setEditSelectedProductIds(
      planData.productsInThisPlan
        ? planData.productsInThisPlan
        : planData.request.response.products.filter(p => p.isAffected === true).map(p => p.id)
    );
    setEditExitDate(parseDate(planData.exitDate));
    setEditReplacementPlan(planData.replacementPlan || '');
    setEditAdditionalComments(planData.additionalComments || '');
    setEditEndOfProductionDate(parseDate(planData.endOfProductionDate));
    setEditEndOfImportationDate(parseDate(planData.endOfImportationDate));
    setEditLiquidationPlan(planData.liquidationPlan || '');
    setEditFormErrors({});
  }, [planData, editMode]);

  // Add local state for support fields in edit mode
  const [editNeedsSupport, setEditNeedsSupport] = useState(false);
  const [editMarketingSupport, setEditMarketingSupport] = useState(false);
  const [editSupplyChainSupport, setEditSupplyChainSupport] = useState(false);
  const [editSelectedMarketingMember, setEditSelectedMarketingMember] = useState(null);
  const [editSelectedSupplyChainMember, setEditSelectedSupplyChainMember] = useState(null);

  // Track if support fields are disabled (if reviewed)
  const supportDisabled = planData?.reviewStatus === 'approved' || planData?.reviewStatus === 'rejected';

  // Remove this effect (it causes support fields to reset on every planData/editMode change):
  // useEffect(() => {
  //   if (!planData) return;
  //   setEditNeedsSupport(!!planData.needsSupport);
  //   setEditMarketingSupport(!!planData.marketingSupport);
  //   setEditSupplyChainSupport(!!planData.supplyChainSupport);
  //   setEditSelectedMarketingMember(planData.selectedMarketingMember || null);
  //   setEditSelectedSupplyChainMember(planData.selectedSupplyChainMember || null);
  // }, [planData, editMode]);

  // Instead, set support fields only when entering edit mode (and snapshot) in a single effect:
  useEffect(() => {
    if (editMode && planData) {
      // Use productsInThisPlan if available, otherwise fallback to impacted products
      setEditSelectedProductIds(
        planData.productsInThisPlan
          ? planData.productsInThisPlan
          : planData.request.response.products.filter(p => p.isAffected === true).map(p => p.id)
      );
      setEditExitDate(parseDate(planData.exitDate));
      setEditReplacementPlan(planData.replacementPlan || '');
      setEditAdditionalComments(planData.additionalComments || '');
      setEditEndOfProductionDate(parseDate(planData.endOfProductionDate));
      setEditEndOfImportationDate(parseDate(planData.endOfImportationDate));
      setEditLiquidationPlan(planData.liquidationPlan || '');
      setEditFormErrors({});
      // Set support fields
      setEditNeedsSupport(!!planData.needsSupport);
      setEditMarketingSupport(!!planData.marketingSupport);
      setEditSupplyChainSupport(!!planData.supplyChainSupport);
      setEditSelectedMarketingMember(planData.selectedMarketingMember || null);
      setEditSelectedSupplyChainMember(planData.selectedSupplyChainMember || null);
      // Set snapshot
      setInitialPlanSnapshot({
        exitDate: parseDate(planData.exitDate),
        replacementPlan: planData.replacementPlan,
        additionalComments: planData.additionalComments,
        endOfProductionDate: parseDate(planData.endOfProductionDate),
        endOfImportationDate: parseDate(planData.endOfImportationDate),
        liquidationPlan: planData.liquidationPlan,
        selectedProductIds: planData.productsInThisPlan
          ? [...planData.productsInThisPlan]
          : planData.request.response.products.filter(p => p.isAffected === true).map(p => p.id),
        needsSupport: !!planData.needsSupport,
        marketingSupport: !!planData.marketingSupport,
        supplyChainSupport: !!planData.supplyChainSupport,
        selectedMarketingMember: planData.selectedMarketingMember || null,
        selectedSupplyChainMember: planData.selectedSupplyChainMember || null,
      });
    } else if (!editMode) {
      setInitialPlanSnapshot(null);
    }
    // eslint-disable-next-line
  }, [editMode, planData]);

  // Remove review comments immediately if support fields change in edit mode
  useEffect(() => {
    if (!editMode) return;
    if (!planData) return;
    // Only clear if support fields changed compared to planData
    const supportChanged =
      editNeedsSupport !== !!planData.needsSupport ||
      editMarketingSupport !== !!planData.marketingSupport ||
      editSupplyChainSupport !== !!planData.supplyChainSupport ||
      (editSelectedMarketingMember?.id || null) !== (planData.selectedMarketingMember?.id || null) ||
      (editSelectedSupplyChainMember?.id || null) !== (planData.selectedSupplyChainMember?.id || null);
    if (supportChanged && planData.reviewComments) {
      setPlanData(prev => ({
        ...prev,
        reviewComments: ''
      }));
    }
    // eslint-disable-next-line
  }, [
    editNeedsSupport,
    editMarketingSupport,
    editSupplyChainSupport,
    editSelectedMarketingMember,
    editSelectedSupplyChainMember,
    editMode,
    planData
  ]);

  // Save a snapshot of the initial state when entering edit mode
  useEffect(() => {
    if (editMode && planData) {
      setInitialPlanSnapshot({
        exitDate: parseDate(planData.exitDate),
        replacementPlan: planData.replacementPlan,
        additionalComments: planData.additionalComments,
        endOfProductionDate: parseDate(planData.endOfProductionDate),
        endOfImportationDate: parseDate(planData.endOfImportationDate),
        liquidationPlan: planData.liquidationPlan,
        selectedProductIds: planData.productsInThisPlan
          ? [...planData.productsInThisPlan]
          : planData.request.response.products.filter(p => p.isAffected === true).map(p => p.id),
        needsSupport: !!planData.needsSupport,
        marketingSupport: !!planData.marketingSupport,
        supplyChainSupport: !!planData.supplyChainSupport,
        selectedMarketingMember: planData.selectedMarketingMember || null,
        selectedSupplyChainMember: planData.selectedSupplyChainMember || null,
      });
    } else if (!editMode) {
      setInitialPlanSnapshot(null); // Reset snapshot when leaving edit mode
    }
    // eslint-disable-next-line
  }, [editMode, planData]);

  // Dirty check: enable update button only if any field or selection changed
  const isDirty = (() => {
    if (!editMode || !initialPlanSnapshot) return false;
    const dateEqual = (a, b) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      // Compare as YYYY-MM-DD string
      const aStr = a instanceof Date ? a.toISOString().split('T')[0] : a;
      const bStr = b instanceof Date ? b.toISOString().split('T')[0] : b;
      return aStr === bStr;
    };
    const arrEqual = (a, b) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((v, i) => v === sortedB[i]);
    };
    // Helper: treat undefined and '' as equal for text fields
    const textEqual = (a, b) => (a ?? '') === (b ?? '');
    // Helper: compare support fields (deep compare for member objects)
    const supportEqual = (a, b) => {
      if (!!a.needsSupport !== !!b.needsSupport) return false;
      if (!!a.marketingSupport !== !!b.marketingSupport) return false;
      if (!!a.supplyChainSupport !== !!b.supplyChainSupport) return false;
      // Compare member by id and name (in case id is not present)
      const marketingMemberA = a.selectedMarketingMember;
      const marketingMemberB = b.selectedMarketingMember;
      const supplyChainMemberA = a.selectedSupplyChainMember;
      const supplyChainMemberB = b.selectedSupplyChainMember;
      const memberEqual = (m1, m2) => {
        if (!m1 && !m2) return true;
        if (!m1 || !m2) return false;
        return (m1.id ?? m1.name ?? '') === (m2.id ?? m2.name ?? '');
      };
      if (!memberEqual(marketingMemberA, marketingMemberB)) return false;
      if (!memberEqual(supplyChainMemberA, supplyChainMemberB)) return false;
      return true;
    };

    // If support is requested, require at least one member selected
    if (
      editNeedsSupport &&
      !(
        (editMarketingSupport && editSelectedMarketingMember) ||
        (editSupplyChainSupport && editSelectedSupplyChainMember)
      )
    ) {
      return false;
    }

    // If a team is selected but no member is selected for that team, disable update
    if (
      (editMarketingSupport && !editSelectedMarketingMember) ||
      (editSupplyChainSupport && !editSelectedSupplyChainMember)
    ) {
      return false;
    }

    // --- DIRTY if any field or support selection changed ---
    return (
      !dateEqual(editExitDate, initialPlanSnapshot.exitDate) ||
      !dateEqual(editEndOfProductionDate, initialPlanSnapshot.endOfProductionDate) ||
      !dateEqual(editEndOfImportationDate, initialPlanSnapshot.endOfImportationDate) ||
      !textEqual(editReplacementPlan, initialPlanSnapshot.replacementPlan) ||
      !textEqual(editLiquidationPlan, initialPlanSnapshot.liquidationPlan) ||
      !textEqual(editAdditionalComments, initialPlanSnapshot.additionalComments) ||
      !arrEqual(editSelectedProductIds, initialPlanSnapshot.selectedProductIds) ||
      // Compare support fields deeply (including member selection)
      !supportEqual(
        {
          needsSupport: editNeedsSupport,
          marketingSupport: editMarketingSupport,
          supplyChainSupport: editSupplyChainSupport,
          selectedMarketingMember: editSelectedMarketingMember,
          selectedSupplyChainMember: editSelectedSupplyChainMember,
        },
        {
          needsSupport: initialPlanSnapshot.needsSupport,
          marketingSupport: initialPlanSnapshot.marketingSupport,
          supplyChainSupport: initialPlanSnapshot.supplyChainSupport,
          selectedMarketingMember: initialPlanSnapshot.selectedMarketingMember,
          selectedSupplyChainMember: initialPlanSnapshot.selectedSupplyChainMember,
        }
      )
    );
  })();

  // Helper: all impacted products
  const impactedProducts = planData?.request?.response?.products?.filter(p => p.isAffected === true) || [];

  // Helper: all products
  const allProducts = planData?.request?.response?.products || [];

  // Helper: products selected in edit mode
  const selectedProducts = allProducts.filter(p => editSelectedProductIds.includes(p.id));

  // Helper: products not selected but impacted
  const unselectedImpactedProducts = impactedProducts.filter(p => !editSelectedProductIds.includes(p.id));

  // Edit mode: handle select/unselect product
  const handleToggleProduct = (productId) => {
    setEditSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.length > 1 ? prev.filter(id => id !== productId) : prev // must keep at least 1
        : [...prev, productId]
    );
  };

  // Edit mode: validate and update
  const handleUpdatePlan = () => {
    // Simple validation
    let errors = {};
    if (planData.request.restrictionType === 'banning') {
      if (!editExitDate) errors.exitDate = "Exit date is required";
      if (!editReplacementPlan) errors.replacementPlan = "Replacement plan is required";
    } else {
      // At least one date is required
      if (!editEndOfProductionDate && !editEndOfImportationDate) {
        errors.dates = "At least one date is required";
      }
      if (!editLiquidationPlan) errors.liquidationPlan = "Liquidation plan is required";
    }
    if (editSelectedProductIds.length === 0) errors.products = "At least one impacted product must be selected";
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Simulate update (replace with API call if needed)
    setPlanData(prev => ({
      ...prev,
      exitDate: editExitDate ? editExitDate.toISOString().split('T')[0] : null,
      replacementPlan: editReplacementPlan,
      additionalComments: editAdditionalComments,
      endOfProductionDate: editEndOfProductionDate ? editEndOfProductionDate.toISOString().split('T')[0] : null,
      endOfImportationDate: editEndOfImportationDate ? editEndOfImportationDate.toISOString().split('T')[0] : null,
      liquidationPlan: editLiquidationPlan,
      productsInThisPlan: editSelectedProductIds,
      // Reflect support fields
      needsSupport: editNeedsSupport,
      marketingSupport: editMarketingSupport,
      supplyChainSupport: editSupplyChainSupport,
      selectedMarketingMember: editSelectedMarketingMember,
      selectedSupplyChainMember: editSelectedSupplyChainMember,
      // If any changes, set reviewStatus to 'pending'
      reviewStatus: 'pending'
    }));
    setEditMode(false);
    setSnackbarOpen(true);
  };

  const handleDeleteClick = () => {
    setDeletePopupOpen(true);
  };
  const handleClosePopup = () => {
    setDeletePopupOpen(false);
  };
  const handleConfirmDelete = () => {
    setDeleteLoading(true);
    setTimeout(() => {
      setDeleteLoading(false);
      setDeletePopupOpen(false);
      dispatch(setSnackbar({ open: true, message: "Exit plan deleted successfully!", error: false }));
      navigate(currentUserRole === "RD_MANAGER" ? DefaultRoutes.rdManagerExitPlans : DefaultRoutes.rdExitPlans);
    }, 1200);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingComponent size="5em" thickness={5} color="primary" />
      </Box>
    );
  }

  const contentContainerStyles = {
    display: 'flex',
    gap: 1,
    flex: 1,
    overflow: 'hidden',
    mt: 1,
    height: 'calc(100vh - 110px)'
  };

  const leftColumnStyles = {
    flex: '0 0 35%',
    display: 'flex',
    flexDirection: 'column',
  };

  const tabsStyles = (theme) => ({
    borderBottom: 0,
    borderColor: 'divider',
    '& .MuiTab-root': {
      minWidth: 0,
      px: 1.5,
      py: 0.75,
      fontSize: '0.8rem',
      textTransform: 'none'
    },
    '& .Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 500
    }
  });

  const tabContentStyles = {
    flex: 1,
    overflow: 'auto'
  };

  const rightColumnStyles = {
    flex: '1 1 65%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto'
  };

  // Helper: safely format date (handles null, undefined, Date, or invalid)
  const safeFormatDate = (date) => {
    if (!date) return "Not specified";
    if (typeof date === 'string') {
      // Try to parse and format as YYYY-MM-DD if possible
      const d = new Date(date);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
      return date;
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return "Not specified";
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <PageTitle
          title="Exit Plan"
          PageIcon={<EditCalendarRoundedIcon color="white" fontSize="large"/>}
          iconColor={themeCreator().palette.primary.main}
          noMargin
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canEdit() && (
            <FormControlLabel
              control={
                <ModeSwitch
                  checked={editMode}
                  onChange={(_, checked) => setEditMode(checked)}
                />
              }
              label={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 0.5, 
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: theme.palette.primary.main
                }}>
                  <EditIcon sx={{ fontSize: '0.9rem' }} />
                  <Typography component="span" sx={{ fontSize: '0.75rem' }}>Edit</Typography>
                </Box>
              }
              labelPlacement="end"
              sx={{ m: 0 }}
            />
          )}
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{ ml: 1, textTransform: 'none', borderRadius: '8px', px: 2 }}
          >
            Delete
          </Button>
        </Box>
      </HeaderContainer>

      <Box sx={contentContainerStyles}>
        {/* Left Column with tabs */}
        <Box sx={leftColumnStyles}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={tabsStyles(theme)}
            variant="fullWidth"
          >
            <Tab label="Request Details" />
            <Tab label="Products" />
            {/* Show Support Request tab in edit mode, Support Details otherwise */}
            {editMode
              ? <Tab label="Support Request" />
              : <Tab label="Support" />
            }
            {!editMode && <Tab label="Plan Status" />}
          </Tabs>
          
          <Box sx={tabContentStyles}>
            {tabValue === 0 && (
              <RequestDetailsTab requestData={planData.request} theme={theme} />
            )}
          
            {tabValue === 1 && (
              <StyledCard sx={{...cardTransition(theme), flexShrink: 0, display: 'flex', flexDirection: 'column'}}>
                <CardContent sx={{...cardContentStyles, display: 'flex', flexDirection: 'column', p: 2}}>
                  <AffectedProductsTab
                    affectedProducts={editMode
                      ? allProducts.filter(p => p.isAffected === true)
                      : allProducts}
                    theme={theme}
                    selectedProductIds={editMode
                      ? editSelectedProductIds
                      : allProducts.filter(p => p.isAffected === true && (planData.productsInThisPlan ? planData.productsInThisPlan.includes(p.id) : editSelectedProductIds.includes(p.id))).map(p => p.id)
                    }
                    onSelectProduct={
                      editMode
                        ? handleToggleProduct
                        : () => {}
                    }
                    plannedProductIds={[]} // disables checkboxes for planned products if needed
                    readOnly={!editMode}
                  />
                  {editMode && editFormErrors.products && (
                    <Typography variant="caption" color="error">{editFormErrors.products}</Typography>
                  )}
                </CardContent>
              </StyledCard>
            )}
          
            {/* Support tab: show SupportRequestTab in edit mode, SupportDetailsTab otherwise */}
            {tabValue === 2 && editMode && (
              <StyledCard sx={{...cardTransition(theme), flexShrink: 0}}>
                <CardContent sx={cardContentStyles}>
                  {/* New Support Requests Section */}
                  <SupportTab
                      exitPlanId={planId}
                      isEditable={!supportDisabled}
                      disabled={supportDisabled}
                      planStatus={planData?.status}
                  />
                  
                  {/* Legacy Support Request Section */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Legacy Support Request
                    </Typography>
                    <SupportRequestTab
                      needsSupport={editNeedsSupport}
                      marketingSupport={editMarketingSupport}
                      supplyChainSupport={editSupplyChainSupport}
                      selectedMarketingMember={editSelectedMarketingMember}
                      selectedSupplyChainMember={editSelectedSupplyChainMember}
                      setNeedsSupport={supportDisabled ? undefined : setEditNeedsSupport}
                      setMarketingSupport={supportDisabled ? undefined : setEditMarketingSupport}
                      setSupplyChainSupport={supportDisabled ? undefined : setEditSupplyChainSupport}
                      setSelectedMarketingMember={supportDisabled ? undefined : setEditSelectedMarketingMember}
                      setSelectedSupplyChainMember={supportDisabled ? undefined : setEditSelectedSupplyChainMember}
                      formErrors={editFormErrors}
                      theme={theme}
                      disabled={supportDisabled}
                    />
                    {supportDisabled && (
                      <Typography variant="caption" color="error" sx={{ mt: 2 }}>
                        Support request cannot be changed after review.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            )}

            {tabValue === 2 && !editMode && (
              <StyledCard sx={{...cardTransition(theme), flexShrink: 0}}>
                <CardContent sx={cardContentStyles}>
                  {/* New Support Requests Section */}
                  <SupportTab
                      exitPlanId={planId}
                      isEditable={false}
                      disabled={true}
                      planStatus={planData?.status}
                  />
                  
                  {/* Legacy Support Details Section */}
                  {(planData.marketingSupport || planData.supplyChainSupport) && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        Legacy Support Details
                      </Typography>
                      <SupportDetailsTab
                        needsSupport={planData.needsSupport}
                        marketingSupport={planData.marketingSupport}
                        supplyChainSupport={planData.supplyChainSupport}
                        selectedMarketingMember={planData.selectedMarketingMember}
                        selectedSupplyChainMember={planData.selectedSupplyChainMember}
                        marketingSupportProvided={planData.marketingSupportProvided}
                        supplyChainSupportProvided={planData.supplyChainSupportProvided}
                        comments={planData.comments}
                        reviewStatus={planData.reviewStatus}
                        reviewComments={planData.reviewComments}
                        theme={theme}
                      />
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            )}

            {!editMode && tabValue === 3 && (
              <StyledCard sx={{...cardTransition(theme), flexShrink: 0}}>
                <CardContent
                    sx={{
                      ...cardContentStyles,
                      flex: 1,
                      overflow: 'auto',
                      minHeight: 0,
                      pt: 1,
                      pb: 0,
                      pl: 1,
                      pr: 1,
                    }}
                >
                  <PlanStatusTab
                    exitPlanStatus={planData.exitPlanStatus}
                    reviewStatus={planData.reviewStatus}
                    createdAt={planData.createdAt}
                    updatedAt={planData.updatedAt}
                    primaryDate={planData.request.primaryDate}
                    primaryDateType={planData.request.primaryDateType}
                    theme={theme}
                  />
                </CardContent>
              </StyledCard>
            )}
          </Box>
        </Box>

        {/* Right Column with plan details */}
        <Box sx={rightColumnStyles}>
          <StyledCard sx={{...cardTransition(theme), height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Sticky Title */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: theme.palette.background.paper,
                pb: 0,
                mb: 0
              }}
            >
              <SectionTitle variant="h6" sx={sectionTitleStyles(theme)}>
                Exit Plan Details
              </SectionTitle>
            </Box>
            {/* Products in this Plan section - always under the title */}
            <Box sx={{ mb: 1, px: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Products in this Plan:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(editMode
                   ? allProducts.filter(p => p.isAffected === true && editSelectedProductIds.includes(p.id))
                  : allProducts.filter(p => p.isAffected === true && (planData.productsInThisPlan ? planData.productsInThisPlan.includes(p.id) : editSelectedProductIds.includes(p.id)))
                ).map(product => (
                  <Chip
                    key={product.id}
                    label={product.identifier}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 500 }}
                  />
                ))}
                {(editMode
                  ? allProducts.filter(p => p.isAffected === true && editSelectedProductIds.includes(p.id)).length === 0
                  : allProducts.filter(p => p.isAffected === true && (planData.productsInThisPlan ? planData.productsInThisPlan.includes(p.id) : editSelectedProductIds.includes(p.id))).length === 0
                ) && (
                  <Typography variant="body2" color="text.secondary">
                    No products selected.
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Scrollable details/form section */}
            <CardContent
              sx={{
                ...cardContentStyles,
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                pt: 1,
                pb: 0,
                pl: 1,
                pr: 1,
              }}
            >
              {!editMode ? (
                <ExitPlanDetailsSection
                  planData={planData}
                  theme={theme}
                  safeFormatDate={safeFormatDate}
                />
              ) : (
                <ExitPlanFormSection
                  requestData={planData.request}
                  formErrors={editFormErrors}
                  exitDate={editExitDate}
                  endOfProductionDate={editEndOfProductionDate}
                  endOfImportationDate={editEndOfImportationDate}
                  replacementPlan={editReplacementPlan}
                  liquidationPlan={editLiquidationPlan}
                  additionalComments={editAdditionalComments}
                  needsSupport={planData.needsSupport}
                  setExitDate={setEditExitDate}
                  setEndOfProductionDate={setEditEndOfProductionDate}
                  setEndOfImportationDate={setEditEndOfImportationDate}
                  setReplacementPlan={setEditReplacementPlan}
                  setLiquidationPlan={setEditLiquidationPlan}
                  setAdditionalComments={setEditAdditionalComments}
                  handleSubmit={handleUpdatePlan}
                  isEditing={true}
                  submitting={false}
                  theme={theme}
                  disableSubmit={!isDirty}
                  showDelete={false}
                  showSupportAlert={false}
                />
              )}
            </CardContent>
          </StyledCard>
        </Box>
      </Box>
      {/* Snackbar for update success */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          Exit plan updated successfully!
        </MuiAlert>
      </Snackbar>
      {DeletePopup(
        "exit plan",
        deletePopupOpen,
        handleClosePopup,
        handleConfirmDelete,
        planId,
        deleteLoading,
        "Delete"
      )}
    </PageContainer>
  );
};

// Component to display the exit plan details
const ExitPlanDetailsSection = ({ planData, theme, safeFormatDate }) => {
  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return "Not specified";
    
    // If date is already a string, return it
    if (typeof date === 'string') return date;
    
    // If date is a Date object, format it to YYYY-MM-DD
    return date instanceof Date 
      ? date.toISOString().split('T')[0] 
      : "Invalid date";
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack spacing={1} sx={{ mb: 0 }}>
            {/* Show End of Production Date and End of Importation Date side by side */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0 }}>
                  End of Production Date
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: theme.palette.action.hover
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                    {safeFormatDate(planData.endOfProductionDate)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0 }}>
                  End of Importation Date
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: theme.palette.action.hover
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                    {safeFormatDate(planData.endOfImportationDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Liquidation Plan field - now with popup */}
            <PlanViewerWithPopup 
              title="Liquidation Plan"
              content={planData.liquidationPlan}
              height={220}
            />

        {/* Additional Comments field */}
        {planData.additionalComments && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0 }}>
              Additional Comments
            </Typography>
            <Box 
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 1,
                bgcolor: theme.palette.action.hover,
                height: '80px',
                overflow: 'auto'
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap' }}>
                {planData.additionalComments}
              </Typography>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ExitPlanDetail;
